/**
 * useAnalysisAnalytics — PostHog + Sentry analytics for analysis flow
 *
 * Extracted from useAnalyze.ts as part of E3 refactoring, S3.3 (#743).
 *
 * Provides three tracking functions:
 * 1. trackStart() — Analysis initiated (PostHog, Sentry context, breadcrumbs, transaction)
 * 2. trackSuccess() — Analysis completed successfully
 * 3. trackFailure() — Analysis failed
 *
 * The transaction object is returned from trackStart() and passed to trackSuccess()/trackFailure()
 * to enable Sentry span management across the async analysis flow.
 */

import {
  captureEvent,
  setAnalysisContext,
  clearAnalysisContext,
  addBreadcrumb,
  startTransaction,
} from '../../../lib/analytics';
import { scrubString } from '../../../lib/analytics/scrubbers';
import type { ApiResponse, DramaAnalysisResponse, ApiError } from '@dramadetector/api-client';
import type { ContextType } from '../../../store';

type Transaction = ReturnType<typeof startTransaction>;

/**
 * Analytics tracking for the analysis flow.
 *
 * @returns { trackStart, trackSuccess, trackFailure }
 */
export function useAnalysisAnalytics() {
  /**
   * Track analysis initiation.
   *
   * @param context - Analysis context type (friends, family, etc.)
   * @param message - Message being analyzed (used for length metrics only)
   * @returns Sentry transaction object (pass to trackSuccess/trackFailure)
   */
  function trackStart(context: ContextType, message: string): Transaction {
    // Track analysis start (PostHog - product analytics)
    captureEvent('analysis_started', {
      context,
      message_length: message.length,
    });

    // Phase 2: Set Sentry context for debugging (only visible on errors)
    setAnalysisContext({
      input_length: message.length,
      complexity_tier: message.length < 100 ? 'simple' : message.length < 300 ? 'moderate' : 'complex',
      context_type: context,
    });

    // Phase 3: Add breadcrumb for analysis flow
    addBreadcrumb('state', 'Analysis started', {
      message_length: message.length,
      context,
    });

    // Phase 4: Start performance transaction
    const transaction = startTransaction('analyze_drama', 'analysis.flow');

    // Phase 3: Breadcrumb for API call start
    addBreadcrumb('api', 'Drama analysis API call started', {
      text_length: message.length,
    });

    return transaction;
  }

  /**
   * Track successful analysis completion.
   *
   * @param transaction - Sentry transaction from trackStart()
   * @param response - The analysis API response
   * @param context - Analysis context type
   * @param message - Original message (used for length metrics)
   * @param duration - Total analysis duration in ms
   */
  function trackSuccess(
    transaction: Transaction,
    response: ApiResponse<DramaAnalysisResponse>,
    context: ContextType,
    message: string,
    duration: number
  ): void {
    // Phase 3: Breadcrumb for successful API response
    addBreadcrumb('api', 'Drama analysis API call completed', {
      score: response.data?.score,
      cached: response.data?.metadata?.cached,
      duration_ms: duration,
    });

    // Phase 2: Update Sentry context with result metadata
    setAnalysisContext({
      input_length: message.length,
      complexity_tier: message.length < 100 ? 'simple' : message.length < 300 ? 'moderate' : 'complex',
      cache_attempted: true,
      cache_hit: response.data?.metadata?.cached || false,
      provider: response.data?.metadata?.provider || 'unknown',
      context_type: context,
    });

    // Phase 4: Finish performance span (v8+ API)
    if (transaction) {
      transaction.setAttribute?.('score', response.data?.score ?? undefined);
      transaction.setAttribute?.('cached', response.data?.metadata?.cached);
      transaction.setAttribute?.('provider', response.data?.metadata?.provider);
      transaction.setAttribute?.('latency_ms', response.data?.metadata?.latencyMs);
      transaction.end?.();
    }

    // Track analysis success (PostHog - product analytics)
    captureEvent('analysis_completed', {
      context,
      score: response.data?.score,
      issues_count: response.data?.issues?.length || 0,
      rewrites_count: response.data?.rewrites?.length || 0,
      duration_ms: duration,
      message_length: message.length,
      cached: response.data?.metadata?.cached || false,
      provider: response.data?.metadata?.provider || 'unknown',
      latency_ms: response.data?.metadata?.latencyMs || 0,
      tokens_used: response.data?.metadata?.tokensUsed || 0,
      cost_usd: response.data?.metadata?.costUsd || 0,
    });

    // Track latency separately for performance monitoring (Phase 1.5)
    if (response.data?.metadata) {
      captureEvent('analysis_latency', {
        total_ms: duration,
        api_ms: response.data.metadata.latencyMs || 0,
        cached: response.data.metadata.cached || false,
        provider: response.data.metadata.provider || 'unknown',
      });
    }

    // Track cost separately for budget monitoring (Phase 1.5)
    if (response.data?.metadata?.costUsd) {
      captureEvent('analysis_cost', {
        cost_usd: response.data.metadata.costUsd,
        provider: response.data.metadata.provider || 'unknown',
        tokens_used: response.data.metadata.tokensUsed || 0,
        cached: response.data.metadata.cached || false,
      });
    }

    // Track cache performance (Phase 1.5)
    if (response.data?.metadata?.cached) {
      captureEvent('Cache_Hit', {
        latency_ms: response.data.metadata.latencyMs || 0,
        cost_saved_usd: 0.00005, // Estimated cost saved per cache hit
      });
    }

    // Phase 2: Clear Sentry context after successful analysis
    clearAnalysisContext();
  }

  /**
   * Track analysis failure.
   *
   * @param transaction - Sentry transaction from trackStart()
   * @param err - The error that caused the failure
   * @param context - Analysis context type
   * @param message - Original message (used for length metrics)
   * @param duration - Total analysis duration in ms
   */
  function trackFailure(
    transaction: Transaction,
    err: ApiError | (Error & { code?: string }),
    context: ContextType,
    message: string,
    duration: number
  ): void {
    // Phase 3: Breadcrumb for API failure
    addBreadcrumb('api', 'Drama analysis API call failed', {
      error_message: err.message,
      error_code: err.code,
      duration_ms: duration,
    }, 'error');

    // Phase 4: Finish span with error status (v8+ API)
    if (transaction) {
      transaction.setAttribute?.('error_message', err.message);
      transaction.setStatus?.('unknown_error' as any);
      transaction.end?.();
    }

    // Track analysis failure (PostHog - product analytics)
    // M4.2: Scrub error_message to prevent PII leakage to PostHog
    captureEvent('analysis_failed', {
      context,
      error_message: scrubString(err.message || 'Unknown error'),
      duration_ms: duration,
      message_length: message.length,
    });
  }

  return { trackStart, trackSuccess, trackFailure };
}
